import { ArrowRight, Sparkles } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LandingAgentSignupModal from '../HomePage/sections/LandingAgentSignupModal';
import { useAgentStripCopy } from '../HomePage/sections/useHeroPreviewCopy';
import { useTypewriterPlaceholder } from '../../modules/ai-agent/hooks/useTypewriterPlaceholder';
import { trackCTA } from '../../utils/metaPixel';

export const HOME_AGENT_STICKY_ZONE_ID = 'landing-agent-sticky-zone';
export const MARKETING_AGENT_STICKY_ZONE_ID = 'marketing-agent-sticky-zone';
export const HOME_AGENT_RELEASE_BEFORE_ID = 'try-it-free';

export interface LandingAgentBarProps {
  onNavigate?: (view: string) => void;
  stickyZoneId?: string;
  releaseBeforeId?: string | null;
  /** When true, bar fades out after release (home page before Try it for free). */
  hideOnRelease?: boolean;
}

function getNavOffset(): number {
  const navBar = document.querySelector('.home-landing-nav__bar');
  const navHeight = navBar?.getBoundingClientRect().height ?? 52;
  return navHeight;
}

const LandingAgentBar: React.FC<LandingAgentBarProps> = ({
  stickyZoneId = HOME_AGENT_STICKY_ZONE_ID,
  releaseBeforeId = HOME_AGENT_RELEASE_BEFORE_ID,
  hideOnRelease = true,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pinActive, setPinActive] = useState(false);
  const [released, setReleased] = useState(false);
  const [spacerHeight, setSpacerHeight] = useState(0);

  const updatePinState = useCallback(() => {
    const sentinel = sentinelRef.current;
    const zone = document.getElementById(stickyZoneId);
    const releaseBefore = releaseBeforeId ? document.getElementById(releaseBeforeId) : null;
    const wrap = wrapRef.current;
    if (!sentinel || !zone || !wrap) return;

    const navOffset = getNavOffset();
    const barHeight = wrap.offsetHeight;
    const sentinelTop = sentinel.getBoundingClientRect().top;
    const zoneBottom = zone.getBoundingClientRect().bottom;
    const releaseBeforeTop = releaseBefore?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY;
    const releaseLine = navOffset + barHeight + 12;

    const shouldRelease =
      zoneBottom <= releaseLine ||
      (releaseBeforeId != null && releaseBeforeTop <= window.innerHeight * 0.92);
    const shouldPin = sentinelTop <= navOffset && !shouldRelease;

    setReleased(shouldRelease);
    setPinActive(shouldPin);
    if (shouldPin) {
      setSpacerHeight(barHeight);
    }
  }, [releaseBeforeId, stickyZoneId]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;

    const measure = () => {
      setSpacerHeight(wrap.offsetHeight);
      updatePinState();
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(wrap);

    window.addEventListener('scroll', updatePinState, { passive: true });
    window.addEventListener('resize', measure);

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', updatePinState);
      window.removeEventListener('resize', measure);
    };
  }, [updatePinState]);

  const agentStrip = useAgentStripCopy();
  const typewriter = useTypewriterPlaceholder(agentStrip.queries, !focused && !query);
  const inputPlaceholder = focused || query
    ? t('home.hero.agentDock.inputPlaceholder')
    : typewriter || agentStrip.queries[0];

  const openSignupModal = useCallback(() => {
    trackCTA('landing_agent_dock_type');
    setModalOpen(true);
  }, []);

  const handleFocus = () => { setFocused(true); openSignupModal(); };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    if (!modalOpen) openSignupModal();
  };
  const handleFindJobs = (e?: React.FormEvent) => {
    e?.preventDefault();
    trackCTA('landing_agent_strip_find_jobs');
    openSignupModal();
  };

  const wrapClassName = [
    'landing-agent-inline-wrap',
    pinActive ? 'landing-agent-inline-wrap--pinned' : '',
    pinActive ? 'landing-agent-inline-wrap--is-stuck' : '',
    released && hideOnRelease ? 'landing-agent-inline-wrap--released' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div ref={sentinelRef} className="landing-agent-sticky-sentinel" aria-hidden />
      <div
        className="landing-agent-sticky-spacer"
        aria-hidden
        style={{ height: pinActive ? spacerHeight : 0 }}
      />
      <div ref={wrapRef} className={wrapClassName}>
        <div className="landing-agent-inline">
          <form className="landing-agent-inline__bar" onSubmit={handleFindJobs}>
            <span className="landing-agent-inline__icon" aria-hidden>
              <Sparkles className="h-4 w-4" strokeWidth={1.75} />
            </span>
            <span className="landing-agent-inline__sep" aria-hidden />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={() => setFocused(false)}
              placeholder={inputPlaceholder}
              className="landing-agent-inline__field"
              aria-label={t('home.hero.agentDock.inputPlaceholder')}
            />
            {!query && !focused && (
              <span className="landing-agent-inline__hint" aria-hidden>
                {t('home.hero.agentDock.typeHint')}
              </span>
            )}
            <button type="submit" className="landing-agent-inline__cta">
              {t('home.hero.agentDock.findJobs')}
              <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
            </button>
          </form>
        </div>
      </div>

      <LandingAgentSignupModal
        open={modalOpen}
        draftQuery={query}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

export default LandingAgentBar;
